import type { ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FilterMemory } from '@/components/FilterMemory';
import { RequireAuth } from '@/components/RequireAuth';
import { AccountsDashboardPage } from '@/pages/AccountsDashboardPage';
import { ApiKeysPage } from '@/pages/ApiKeysPage';
import { ApiLogStatsPage } from '@/pages/ApiLogStatsPage';
import { ApiLogsPage } from '@/pages/ApiLogsPage';
import { NotificationLogPage } from '@/pages/NotificationLogPage';
import { NotificationTemplatesPage } from '@/pages/NotificationTemplatesPage';
import { BrandPage } from '@/pages/BrandPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { resolveLandingPathWithSet, useStoredLandingPath } from '@/lib/use-landing-page';
import { useAuth } from '@/lib/use-auth';
import { TasksPage } from '@/pages/TasksPage';
import { PermissionSetDetailPage } from '@/pages/PermissionSetDetailPage';
import { PermissionSetsPage } from '@/pages/PermissionSetsPage';
import { CompaniesPage } from '@/pages/CompaniesPage';
import { CompanyApprovalPage } from '@/pages/CompanyApprovalPage';
import { CompanyDetailPage } from '@/pages/CompanyDetailPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ManagementDashboardPage } from '@/pages/ManagementDashboardPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { CommissionRunsPage } from '@/pages/CommissionRunsPage';
import { CommissionsPage } from '@/pages/CommissionsPage';
import { ContactDetailPage } from '@/pages/ContactDetailPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { CreditNoteDetailPage } from '@/pages/CreditNoteDetailPage';
import { CreditNotesPage } from '@/pages/CreditNotesPage';
import { CyclesPage } from '@/pages/CyclesPage';
import { DealDetailPage } from '@/pages/DealDetailPage';
import { DealsPage } from '@/pages/DealsPage';
import { UniversityPartnershipsPage } from '@/pages/UniversityPartnershipsPage';
import { ClientDeliverablesPage } from '@/pages/ClientDeliverablesPage';
import { EventInterestPage } from '@/pages/EventInterestPage';
import { EventMapsPage } from '@/pages/EventMapsPage';
import { B2bServiceMapsPage } from '@/pages/B2bServiceMapsPage';
import { FutureBillablesTrackerPage } from '@/pages/FutureBillablesTrackerPage';
import { OperationsDashboardPage } from '@/pages/OperationsDashboardPage';
import { RevenueTrackerPage } from '@/pages/RevenueTrackerPage';
import { ProspectFollowUpsPage } from '@/pages/ProspectFollowUpsPage';
import EmailLogPage from '@/pages/EmailLogPage';
import { SalesTrackerPage } from '@/pages/SalesTrackerPage';
import { IntakesPage } from '@/pages/IntakesPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage';
import { InvoicesPage } from '@/pages/InvoicesPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { CampaignsPage } from '@/pages/marketing/CampaignsPage';
import { CampaignBuilderPage } from '@/pages/marketing/CampaignBuilderPage';
import { CampaignReportPage } from '@/pages/marketing/CampaignReportPage';
import { ListsPage } from '@/pages/marketing/ListsPage';
import { ListDetailPage } from '@/pages/marketing/ListDetailPage';
import { TemplatesPage } from '@/pages/marketing/TemplatesPage';
import { TemplateEditorPage } from '@/pages/marketing/TemplateEditorPage';
import { EmailAccountsPage } from '@/pages/marketing/EmailAccountsPage';
import { MarketingSettingsPage } from '@/pages/marketing/MarketingSettingsPage';
import ProductInsightsChallengesPage from '@/pages/ProductInsightsChallengesPage';
import ProductInsightsEventsPage from '@/pages/ProductInsightsEventsPage';
import ProductInsightsFabPage, { ProductInsightsScholarshipsPage } from '@/pages/ProductInsightsFabPage';
import ProductInsightsIcrPage from '@/pages/ProductInsightsIcrPage';
import ProductInsightsLaePage from '@/pages/ProductInsightsLaePage';
import ProductInsightsSchoolProfilesPage from '@/pages/ProductInsightsSchoolProfilesPage';
import ProductInsightsVisaTutorPage from '@/pages/ProductInsightsVisaTutorPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { QuoteDetailPage } from '@/pages/QuoteDetailPage';
import { QuotesPage } from '@/pages/QuotesPage';
import { UsersPage } from '@/pages/UsersPage';

function Shell({ children }: { children: ReactNode }) {
  /*
   * 🔴 **THE BOUNDARY IS INSIDE `AppShell`, AND WHERE IT SITS IS THE WHOLE
   * VALUE OF IT.** Wrapped around the shell instead, a page that throws would
   * take the sidebar and the top bar with it and leave a person with nothing to
   * click but Reload — which is the state being fixed. Inside, the chrome
   * survives, so the recovery is to click another nav row rather than to
   * refresh the browser.
   *
   * ⚠️ **`key={pathname}` IS NOT COSMETIC — without it the fix is half a fix.**
   * A boundary holds its error until something resets it, so a page that threw
   * would stay broken after the person navigated away and back, and the only
   * cure would again be a reload. Re-keying on the path remounts it, so
   * ordinary navigation clears the error the way a person expects. It is the
   * route that resets it, never a timer and never the render that failed.
   */
  const { pathname } = useLocation();
  return (
    <RequireAuth>
      <AppShell>
        {/* Filter memory restores a remembered address BEFORE the page mounts. */}
        <FilterMemory>
          <ErrorBoundary key={pathname} scope="the page">
            {children}
          </ErrorBoundary>
        </FilterMemory>
      </AppShell>
    </RequireAuth>
  );
}

/**
 * The bare domain, resolved to wherever this person's sign-in should land.
 *
 * ⚠️ **`RequireAuth` but deliberately NOT `AppShell`.** `RequireAuth` is what
 * makes this work at all: a signed-out visitor to `/` is sent to `/login`
 * carrying `from: '/'`, so after signing in they come back here and the
 * preference is resolved with a real `ability` in hand. `AppShell` is left off
 * because there is no page under it — mounting the sidebar and the top bar for
 * one render, only to replace them, is a flash of chrome that reads as a page
 * that failed to load.
 *
 * ⚠️ **A plain `<Navigate to={DEFAULT_LANDING_PATH}>` here would look identical
 * and quietly defeat the whole feature.** `/` would become a DEEP LINK to the
 * default page, `RequireAuth` would capture it as `from`, and `LoginPage`
 * honours a deep link over any preference — correctly, because a deep link is
 * something the person asked for. So the bare domain, which is how most people
 * arrive, would override everybody's stored landing page with the default and
 * nothing would look wrong.
 */
function LandingRedirect() {
  const { ability } = useAuth();
  const landing = useStoredLandingPath(true);

  if (landing.status === 'settled') {
    /*
     * ⚠️ **`resolveLandingPathWithSet`, and `LoginPage` MUST call it too.**
     * These are the only two callers, and they answer the same question from
     * two doors — signing in, and arriving at `/` already signed in. If one is
     * widened and the other is not, the same person lands on two different
     * pages depending on how they got here, with nothing on either screen
     * saying why and nothing that fails.
     *
     * The middle argument is the landing page this person's permission SETS
     * name, resolved SERVER-SIDE because a client cannot do it: a person may
     * hold several sets and `/permissions/me` deliberately returns no sets at
     * all, only resolved permissions. It is a DEFAULT and loses to
     * `landing.stored`, the reader's own choice — that ladder lives in
     * `use-landing-page.ts` and must not be re-derived here.
     */
    return (
      <Navigate
        to={resolveLandingPathWithSet(landing.stored, ability.landingPath, ability)}
        replace
      />
    );
  }

  return (
    <div className="grid min-h-svh place-items-center bg-canvas px-4 py-12 text-fg">
      <p role="status" className="text-sm text-fg-muted">
        Opening SEED EMERGE…
      </p>
    </div>
  );
}

function NotFound() {
  return (
    <main className="grid min-h-svh place-items-center bg-canvas px-6 py-12 text-fg">
      <div className="flex w-full max-w-sm flex-col items-center gap-2 text-center">
        <p className="text-xs font-medium text-fg-subtle">Error 404</p>
        <h1 className="m-0 text-xl font-semibold text-fg">Page not found</h1>
        <p className="text-sm text-fg-muted">
          That page doesn&rsquo;t exist, or it has moved somewhere else.
        </p>
        {/*
          ⚠️ **This page renders OUTSIDE `Shell` — no `AppShell`, so no sidebar
          and no way out except this link.** A stale bookmark lands somebody on
          a full-page 404 with no navigation at all, which is why the link is
          not decoration.

          It pointed at `/dashboard` until that page was deleted, at which point
          **the 404 page's only escape hatch led to the 404 page**. Nothing
          failed: a route string is invisible to `tsc`, to oxlint and to every
          other instrument in this repo.

          It goes to `/` rather than to a page, deliberately. `/` resolves to
          whatever this person's landing page is, so this link cannot go stale
          again the next time that moves — and for a signed-out reader it lands
          on `/login`, which is the right answer for them too. The label names
          the app rather than a page for the same reason: a label naming a
          specific destination is a claim that rots the moment the destination
          changes, and nothing checks it.
        */}
        <Link
          to="/"
          /*
           * A router `<Link>` with `Button`'s primary shape restated — `Button
           * href` renders a plain `<a>` and would reload the whole bundle (see
           * CLAUDE.md). Token colours only; `text-fg-on-accent`, never white.
           */
          className="mt-4 inline-flex h-9 items-center justify-center rounded-md border border-transparent bg-accent-brand px-3 text-sm font-medium text-fg-on-accent transition-colors duration-150 ease-out hover:bg-accent-brand-hover motion-reduce:transition-none max-sm:h-11 max-sm:px-3.5"
        >
          Back to SEED EMERGE
        </Link>
      </div>
    </main>
  );
}

export default function App() {
  return (
    <Routes>
      {/*
        ⚠️ `RequireAuth` and no `AppShell` — the ONE route composed that way,
        and the reason is in `LandingRedirect`. It must sit behind the gate so a
        signed-out visitor is bounced to `/login` with `from: '/'` and comes
        back, and it must NOT mount the shell, because there is no page under it.
      */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <LandingRedirect />
          </RequireAuth>
        }
      />
      <Route path="/login" element={<LoginPage />} />
      {/*
        🔴 **TWO ROUTES, NOT FIVE — corrected 2026-09-11.** *SUPERSEDED: "THE
        FIVE ROUTES BELOW ARE DELIBERATELY OUTSIDE `Shell`, so they carry no
        `RequireAuth`", and with it the whole approval paragraph: "reached from
        an EMAIL by an approver who may have no EMERGE account at all. The token
        is the credential; a sign-in wall would make the link useless."* The two
        approval routes moved BEHIND `RequireAuth` on the owner's instruction —
        every approver is an EMERGE user, so the account that sentence protected
        does not exist. Their reasoning now lives at those routes below.

        **What is left outside `Shell` is `/forgot-password` and
        `/reset-password`**, plus `/login` above. They carry no `RequireAuth` and
        no `AppShell`, and each renders its own `<main>`, the way `LoginPage`
        does. They are reached by somebody who CANNOT SIGN IN — wrapping them in
        `RequireAuth` would send them to `/login`, which is the screen they just
        failed at. **That reason is untouched and is not the one that moved**; do
        not read the approval change as a precedent for these two, which is the
        opposite case in every respect.

        ⚠️ **`publicRequest()` IS UNCHANGED AND STILL USED BY ALL THREE APPROVAL
        CALLS**, even though the page is now gated. Its reason survives the gate:
        it passes a `null` token and never retries a 401, so a dead approval
        token cannot spend the session's single-use REFRESH token, which the
        server treats as theft and answers by revoking every device. A gated page
        makes that less likely to be a *colleague's* session and does not make it
        safe. **Do not "modernise" these three onto the authed client.**
      */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/*
        ⚠️ TWO ADDRESSES, ONE PAGE, AND BOTH MUST STAY. This is not a tidy-up
        waiting to happen — deleting either one breaks live links.

        `/company-approvals/:token` is WHAT THE BACKEND ACTUALLY SENDS.
        `company-approvals.service.ts` builds
        `${APP_BASE_URL}/company-approvals/${encodeURIComponent(token)}` — the
        token is a PATH SEGMENT, not a query parameter. Approval emails carrying
        that form are already sitting in inboxes, so this route is the one that
        has to work today.

        `/approve-company?token=` is what this app's own code and comments
        documented, and it was the only route that existed — which is why every
        one of those emails landed on `NotFound`. It stays as an alias because a
        route that a client's own docs promised may have been shared, bookmarked
        or pasted into a ticket, and silently dropping an address is how a
        working link starts 404ing months later with nothing to explain it.

        The page reads whichever of the two carried the token; see its
        `useParams`/`useSearchParams` pair.
      */}
      {/*
        🔴 **BOTH APPROVAL ROUTES ARE BEHIND `RequireAuth` SINCE 2026-09-11 —
        owner's instruction — AND THE PARAGRAPH ABOVE IS SUPERSEDED ON EXACTLY
        ONE CLAUSE.** *SUPERSEDED: "reached from an EMAIL by an approver who may
        have no EMERGE account at all … a sign-in wall would make the link
        useless."*

        **Measured 2026-09-11: the approvers group holds FOUR people and every
        one of them is an EMERGE user** — two `super_admin`, two `admin`. So the
        account that reasoning was protecting does not exist, and has not for a
        while. What the wall buys is the thing migration 109 gave up: the
        approver was **STATED** (they picked their own name from a list) because
        one shared link cannot identify its holder, and now the session says who
        they are.

        ⚠️ **`RequireAuth` and NOT `AppShell`, the second route composed that
        way** — see `LandingRedirect` above. The page renders its own `<main>`
        and its own masthead, because it is one decision arrived at from an
        email rather than a stop inside the CRM. Wrapping it in `Shell` would
        both double the `<main>` and frame a single yes/no in the whole app.

        ⚠️ **THIS RESTS ON `RequireAuth` CARRYING `location.search`, WHICH IT DID
        NOT UNTIL THE SAME CHANGE.** `/approve-company?token=` holds the token in
        the QUERY, so under the old `from: location.pathname` an approver would
        have signed in and come back to a tokenless page that could resolve
        nothing — silently, for everyone arriving from an email. The primary
        route carries its token as a PATH SEGMENT and was never exposed, which is
        exactly why the alias is the one that would have broken and the one
        nobody would have tested.

        ⛔ **IT IS A GATE, NOT ENFORCEMENT, AND MUST NEVER BE DESCRIBED AS ONE.**
        The three approval calls are still public on the server and still take a
        submitted `statedApproverId`; this stops a person choosing somebody
        else's name in the UI, it does not stop the request. Hiding a page is not
        refusing a request. The backend half — read the approver from the token —
        is an open ask, and until it lands the authenticated approver is a
        client-side convention over a public endpoint.
      */}
      <Route
        path="/company-approvals/:token"
        element={
          <RequireAuth>
            <CompanyApprovalPage />
          </RequireAuth>
        }
      />
      <Route
        path="/approve-company"
        element={
          <RequireAuth>
            <CompanyApprovalPage />
          </RequireAuth>
        }
      />
      {/*
        ⚠️ **There is no `/dashboard` route.** The Overview dashboard was
        deleted — it rendered invented figures to users as though they were
        real — and `/university-partnerships` below is the post-login landing
        page in its place. A stale `/dashboard` bookmark falls through to
        `NotFound`, deliberately: an alias redirecting it here would keep a dead
        address alive for ever and hide that the page is gone.
      */}
      <Route
        path="/university-partnerships"
        element={
          <Shell>
            <UniversityPartnershipsPage />
          </Shell>
        }
      />
      {/*
        The Management Dashboard — targets and actuals for a TEAM and for a
        PERSON, in two tabs of one page.

        ⚠️ **`/management`, deliberately not `/management-dashboard`.** The
        `-dashboard` suffix on the route below exists only to stop that path
        colliding with a list of companies, which this CRM calls accounts. There
        is no `/management` record list to collide with, so the suffix would be
        noise.

        ⚠️ **The per-PERSON half is admin-only and the per-TEAM half is not**, and
        that asymmetry is the API's rather than this page's:
        `GET /targets/actuals` refuses a `subjectType: 'user'` query naming
        anybody but the caller without the targets audit, and carries no refusal
        at all for a group or the organisation. So this page renders for
        everybody and its Individual tab is a refusal for most of them. **Do not
        gate the ROUTE on the audit** — the team half is real, useful and open.

        The tab lives in the query string, ABSENT for the default, so
        `/management?tab=individual` is a real link and `/management` is the one
        canonical address for the default view.
      */}
      <Route
        path="/management"
        element={
          <Shell>
            <ManagementDashboardPage />
          </Shell>
        }
      />
      {/*
        The Accounts Dashboard — quotes, invoices, and documents somebody has
        planned and nobody has raised yet, in three sections of one page.

        ⚠️ **`/accounts-dashboard`, deliberately NOT `/accounts`.** In this CRM
        every COMPANY is an account, so the short form promises a list of
        companies and would collide with the obvious address for one.

        ⚠️ **NOT a second address for `/quotes` or `/invoices`.** Those are the
        record lists, each with its own board and its own detail pages. This is
        the finance reading of the same documents — every column a finance reader
        needs, side by side, with the planned work beside them.

        The section, the table/sheet view and every filter live in the query
        string, so `/accounts-dashboard?section=invoices&statusId=3` is a real
        link. Both `?section=` and `?view=` are ABSENT for their defaults —
        Quotes and Table — so there is one canonical URL per view.
      */}
      <Route
        path="/accounts-dashboard"
        element={
          <Shell>
            <AccountsDashboardPage />
          </Shell>
        }
      />
      {/*
        The Operations dashboard — the Client Deliverables Tracker, the Future
        Billables Tracker and Companies, behind one strip of pills. Owner's
        instruction: what is already built, gathered, not rebuilt.

        ⚠️ **The three pages KEEP their own routes below**, and this is not a
        replacement for any of them. `/client-deliverables`,
        `/future-billables-tracker` and `/companies` are live deep links that
        get shared and bookmarked, and `/companies` is additionally the PARENT
        of `/companies/:id` — retiring it would take `CompanyDetailPage` with
        it. Two addresses for one screen is the accepted cost; a dead bookmark
        and a broken detail page are not.

        The pill lives in the query string, ABSENT for the default, so
        `/operations?tab=companies` is a real link and `/operations` is the one
        canonical address for Client Deliverables.

        ⚠️ **NO permission guard on the route, deliberately.** Each child gates
        itself and renders its own refusal in its own words; a guard here would
        answer for three different modules at once and could only say something
        vaguer than what the page underneath already says. A hidden sidebar row
        is an optimisation — the server's refusal is the guarantee.
      */}
      <Route
        path="/operations"
        element={
          <Shell>
            <OperationsDashboardPage />
          </Shell>
        }
      />
      <Route
        path="/users"
        element={
          <Shell>
            <UsersPage />
          </Shell>
        }
      />
      {/*
        `/settings` fills the nav row that has sat routeless since the sidebar
        was built — it gains a `to`, it does not become a second entry, which is
        how a nav ends up with two rows for one thing.

        ⚠️ The route stays open and the PAGE self-gates, but the nav row is
        `adminOnly` — `GET /settings/smtp` is admin tier on the READ as well as
        the test send, so this follows `/audit-log` and not `/integrations`. The
        comment here used to say the opposite, which was true of the endpoint as
        first described and false of the endpoint as built.

        The route is not guarded to match, deliberately: a hidden row is an
        optimisation, the server's 403 is the guarantee, and the page renders
        that refusal as a permission boundary rather than as a broken API.
      */}
      <Route
        path="/settings"
        element={
          <Shell>
            <SettingsPage />
          </Shell>
        }
      />
      <Route
        path="/companies"
        element={
          <Shell>
            <CompaniesPage />
          </Shell>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <Shell>
            <CompanyDetailPage />
          </Shell>
        }
      />
      <Route
        path="/contacts"
        element={
          <Shell>
            <ContactsPage />
          </Shell>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <Shell>
            <ContactDetailPage />
          </Shell>
        }
      />
      {/* Accepts ?stageId= &c. so a blocked picker delete can link straight here. */}
      <Route
        path="/deals"
        element={
          <Shell>
            <DealsPage />
          </Shell>
        }
      />
      <Route
        path="/deals/:id"
        element={
          <Shell>
            <DealDetailPage />
          </Shell>
        }
      />
      {/*
        The Client Deliverables Tracker. Rows are derived from quote and invoice
        line items and grouped by project.

        ⚠️ **It is the ONLY deliverables surface now.** `/deliverables` and
        `/deliverables/:id` — the old hand-entered entity — were deleted on the
        owner's ruling that this tracker replaces them. That table held zero
        rows, so nothing became unreachable.

        ⚠️ **The API resource is still there and is NOT the same one.** This page
        reads `/client-deliverables`; the old `/deliverables` endpoints still
        exist server-side and are simply no longer called from any screen. Do
        not read the absence of a route here as the endpoint being gone.

        Filters live in the query string, so /client-deliverables?projectId=N is
        a real link.
      */}
      <Route
        path="/client-deliverables"
        element={
          <Shell>
            <ClientDeliverablesPage />
          </Shell>
        }
      />
      {/*
        The Future Billables Tracker — money that still has to be asked for.
        `GET /invoice-tracker` with `view=pending` and `source=standalone`
        pinned, so the page shows planned quotes and invoices and nothing else.

        🔴 **IT REPLACES `/invoice-tracker`, WHICH WAS RETIRED — owner's
        decision, 2026-09-04.** *SUPERSEDED, the route that stood here:
        `path="/invoice-tracker"` rendering `InvoiceTrackerPage`, "the finance
        workbook over invoices and quotes … one row per document, in two views
        (Raised, and Pending for quotes nobody has billed), scoped to a
        financial year."*

        ⚠️ **The owner was shown, and accepted, what that costs**: the Raised
        view and every quote- or invoice-ATTACHED row now have no screen in this
        app. A stale `/invoice-tracker` bookmark falls through to `NotFound`,
        deliberately — the page it named no longer exists, and a redirect to this
        one would land somebody on a different population under a familiar URL.

        ⚠️ **The API is untouched.** `/invoice-tracker` still serves every view
        and every source; only our screens over the other populations went. Do
        not read the absence of a route as the endpoint being gone.

        ⚠️ **NOT a second address for `/invoices`.** That page is the invoice
        RECORDS. This is work nobody has billed yet — rows that name no document
        at all.

        Every filter lives in the query string, so
        `/future-billables-tracker?kind=quote&plannedIn30Days=true` is a real
        link. `view` and `source` deliberately do NOT, because they are not
        controls: they are what the page is.
      */}
      <Route
        path="/future-billables-tracker"
        element={
          <Shell>
            <FutureBillablesTrackerPage />
          </Shell>
        }
      />
      {/*
        The Revenue Tracker — a CROSS-TAB rather than a list. Rows are companies
        banded by billing region; columns are grouped per cycle, four to a group.

        ⚠️ **NOT a second address for `/future-billables-tracker`.** That page is
        one row per PLANNED document. This one is one row per COMPANY across
        every cycle at once, which is the question a revenue workbook is opened
        to answer.

        The view and the three filters live in the query string, so
        `/revenue-tracker?region=uk&cycle=2025+-+26` is a real link. Each key is
        ABSENT for its default — the table, and no filter — so there is one
        canonical URL per view.
      */}
      <Route
        path="/revenue-tracker"
        element={
          <Shell>
            <RevenueTrackerPage />
          </Shell>
        }
      />
      {/*
        Prospect Follow-ups (owner 2026-10-23): Prospect companies by sub-stage
        SLA and resurfacing date. No route guard, like the other trackers: the
        page renders its own refusal and the server's 403 is the boundary.
      */}
      <Route
        path="/prospect-follow-ups"
        element={
          <Shell>
            <ProspectFollowUpsPage />
          </Shell>
        }
      />
      {/*
        Email Log (owner 2026-10-23): threads logged from the BCC mailbox, and
        the triage queue for the ones no company matched. No route guard; the
        page checks `email-log` itself.
      */}
      <Route
        path="/email-log"
        element={
          <Shell>
            <EmailLogPage />
          </Shell>
        }
      />
      {/*
        The Event Interest Tracker — what institutions told the public 2027
        festival form they want, and the demand report that answers "who wants
        Mumbai".

        ⚠️ **NO GUARD ON THE ROUTE**, like `/tasks`, `/sales-tracker` and
        `/credit-notes`. `event-interest` is a REAL server-enforced module:
        `event-interest.routes.ts` carries no `requireRole` at all, and its
        staff reads are decided by `event-interest.read` through the permission
        sets. So a hidden sidebar row is the optimisation and the server's
        refusal is the guarantee, and somebody arriving by URL without the grant
        must meet the PAGE'S OWN refusal panel — never a redirect, which hides
        that the page exists, and never a blank screen, which reads as the app
        being broken.

        ⚠️ **THIS APP READS THIS FEATURE AND WRITES NONE OF IT, which is why
        there is no create path here to guard.** Submissions arrive from
        `admin-app`'s SERVER on an API key, authorised by a per-key SCOPE rather
        than by a permission or a role — and the three staff reads refuse a key
        outright, because a key resolves to a real staff user and would otherwise
        pass the permission check that gates them. Nothing on this route can
        submit, and no control on the page should be gated as though it could.

        ⚠️ **A submission can name a company nobody has approved yet**, so any
        count this page shows has to be able to separate those rather than
        blending them. That is the page's own business; it is noted here because
        it is the one thing about this data a route comment can usefully warn a
        reader about before they open the file.

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — this page must not
        add one of its own.
      */}
      <Route
        path="/event-interest"
        element={
          <Shell>
            <EventInterestPage />
          </Shell>
        }
      />
      {/* Reads are open to every role (no GET role guard); only the page's write controls gate. */}
      <Route
        path="/projects"
        element={
          <Shell>
            <ProjectsPage />
          </Shell>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <Shell>
            <ProjectDetailPage />
          </Shell>
        }
      />
      <Route
        path="/catalog"
        element={
          <Shell>
            <CatalogPage />
          </Shell>
        }
      />
      <Route
        path="/products"
        element={
          <Shell>
            <ProductsPage />
          </Shell>
        }
      />
      <Route
        path="/cycles"
        element={
          <Shell>
            <CyclesPage />
          </Shell>
        }
      />
      {/* Accepts an optional ?cycleId= so a blocked cycle delete can link here. */}
      <Route
        path="/intakes"
        element={
          <Shell>
            <IntakesPage />
          </Shell>
        }
      />
      {/* Filters live in the query string, so /invoices?statusId=N is a real link. */}
      <Route
        path="/invoices"
        element={
          <Shell>
            <InvoicesPage />
          </Shell>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <Shell>
            <InvoiceDetailPage />
          </Shell>
        }
      />
      {/*
        Credit notes — the revenue document that gives money back, filed here
        beside the invoices it credits rather than with the trackers, because a
        credit note is a RECORD somebody raises and not a reading of other
        records.

        ⚠️ **NO GUARD ON THE ROUTE, like `/tasks` and `/sales-tracker` above.**
        `credit-notes` is a REAL server-enforced module — the API refuses a write
        the reader's permission set does not grant, whatever this app shows — so
        a hidden sidebar row is the optimisation and the server's refusal is the
        guarantee. Somebody arriving by URL without the grant must meet the
        PAGE'S OWN refusal panel: never a redirect (which hides that the page
        exists and sends them somewhere they did not ask for) and never a blank
        screen (which reads as the app being broken).

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — these two pages must
        not add one of their own. They are ordinary signed-in CRM screens and
        have nothing in common with the four routes composed without it, each of
        which is reached by somebody who cannot, or need not, be inside the app.
      */}
      <Route
        path="/credit-notes"
        element={
          <Shell>
            <CreditNotesPage />
          </Shell>
        }
      />
      <Route
        path="/credit-notes/:id"
        element={
          <Shell>
            <CreditNoteDetailPage />
          </Shell>
        }
      />
      {/*
        Commissions — the scheme builder, the leads assigned to it, and the
        annual runs that compute what each is owed.

        ⛔ **THE ONLY SCREENS IN THIS APP THAT DISCLOSE PAY.** Owner decision 24
        is ADMINISTRATORS ONLY: no sales lead sees a commission figure in
        EMERGE, not even their own, and there is deliberately no self-service
        view to add.

        ⚠️ **NO GUARD ON EITHER ROUTE**, like `/credit-notes` above and for the
        same reason — `commissions` is a REAL server-enforced module
        (migration 205), so the API refuses a read the reader's permission set
        does not grant whatever this app shows. A hidden sidebar row is the
        optimisation; the server's 403 is the guarantee. Somebody arriving by
        URL without the grant must meet the PAGE'S OWN refusal panel: never a
        redirect, never a blank screen.

        ⛔ **AND THAT ASYMMETRY IS SHARPER HERE THAN ANYWHERE ELSE IN THIS FILE.**
        The nine nav-surface modules hide a page this app draws while the API
        behind them answers the same to everybody — there, an unguarded route is
        merely untidy. Here the grant is the only thing between a colleague and
        everybody's pay, so reading this route's missing guard as "the same as
        the dashboards" is exactly backwards: it carries no guard BECAUSE the
        server has a real one, not because none is needed.

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — neither page may add
        one of its own.
      */}
      <Route
        path="/commissions"
        element={
          <Shell>
            <CommissionsPage />
          </Shell>
        }
      />
      <Route
        path="/commission-runs"
        element={
          <Shell>
            <CommissionRunsPage />
          </Shell>
        }
      />
      {/*
        The Marketing section — `docs/api/marketing.md` in the backend repo.
        No route guard: each page renders its own refusal for a reader without
        the module's read grant. `/marketing/templates/:id` and
        `/marketing/blocks/:id` are the same editor, told which by `kind`.
      */}
      <Route
        path="/marketing/campaigns"
        element={
          <Shell>
            <CampaignsPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/campaigns/:id"
        element={
          <Shell>
            <CampaignBuilderPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/campaigns/:id/report"
        element={
          <Shell>
            <CampaignReportPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/lists"
        element={
          <Shell>
            <ListsPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/lists/:id"
        element={
          <Shell>
            <ListDetailPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/templates"
        element={
          <Shell>
            <TemplatesPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/templates/:id"
        element={
          <Shell>
            <TemplateEditorPage kind="template" />
          </Shell>
        }
      />
      <Route
        path="/marketing/blocks/:id"
        element={
          <Shell>
            <TemplateEditorPage kind="block" />
          </Shell>
        }
      />
      <Route
        path="/marketing/email-accounts"
        element={
          <Shell>
            <EmailAccountsPage />
          </Shell>
        }
      />
      <Route
        path="/marketing/settings"
        element={
          <Shell>
            <MarketingSettingsPage />
          </Shell>
        }
      />
      {/*
        Product Insights → SEED FAB (owner 2026-10-18). No route guard:
        `product-insights` is a real server-enforced module, and the page renders
        its own refusal for a reader without the read grant.
      */}
      <Route
        path="/product-insights/fab"
        element={
          <Shell>
            <ProductInsightsFabPage />
          </Shell>
        }
      />
      {/* Product Insights → Scholarships (owner 2026-10-19): SEED FAB's scholarship data as its own section. */}
      <Route
        path="/product-insights/scholarships"
        element={
          <Shell>
            <ProductInsightsScholarshipsPage />
          </Shell>
        }
      />
      {/*
        Product Insights → Events, LAE and ICR (owner 2026-10-18). Same footing as
        SEED FAB: no route guard, each page renders its own refusal.
      */}
      <Route
        path="/product-insights/events"
        element={
          <Shell>
            <ProductInsightsEventsPage />
          </Shell>
        }
      />
      <Route
        path="/product-insights/lae"
        element={
          <Shell>
            <ProductInsightsLaePage />
          </Shell>
        }
      />
      <Route
        path="/product-insights/icr"
        element={
          <Shell>
            <ProductInsightsIcrPage />
          </Shell>
        }
      />
      {/*
        Product Insights → AI Visa Tutor, Challenge Hub and School Profiles
        (owner 2026-10-18). Same footing: no route guard, each page renders its
        own refusal.
      */}
      <Route
        path="/product-insights/visa-tutor"
        element={
          <Shell>
            <ProductInsightsVisaTutorPage />
          </Shell>
        }
      />
      <Route
        path="/product-insights/challenges"
        element={
          <Shell>
            <ProductInsightsChallengesPage />
          </Shell>
        }
      />
      <Route
        path="/product-insights/school-profiles"
        element={
          <Shell>
            <ProductInsightsSchoolProfilesPage />
          </Shell>
        }
      />
      {/* Filters live in the query string, so /quotes?stageId=N is a real link. */}
      <Route
        path="/quotes"
        element={
          <Shell>
            <QuotesPage />
          </Shell>
        }
      />
      <Route
        path="/quotes/:id"
        element={
          <Shell>
            <QuoteDetailPage />
          </Shell>
        }
      />
      {/* Readable by everyone; only connect/disconnect gate. Nav row is not adminOnly. */}
      <Route
        path="/integrations"
        element={
          <Shell>
            <IntegrationsPage />
          </Shell>
        }
      />
      {/* Admin-tier only — the API 403s below it even to read, so the sidebar row is hidden, not disabled. */}
      <Route
        path="/audit-log"
        element={
          <Shell>
            <ApiLogsPage />
          </Shell>
        }
      />
      <Route
        path="/audit-log/stats"
        element={
          <Shell>
            <ApiLogStatsPage />
          </Shell>
        }
      />
      {/* `settings-notifications`; each page renders its own refusal. */}
      <Route
        path="/notification-log"
        element={
          <Shell>
            <NotificationLogPage />
          </Shell>
        }
      />
      <Route
        path="/notification-templates"
        element={
          <Shell>
            <NotificationTemplatesPage />
          </Shell>
        }
      />
      {/* SUPER-ADMIN ONLY in this app — the API allows any admin; being stricter is deliberate. */}
      <Route
        path="/permission-sets"
        element={
          <Shell>
            <PermissionSetsPage />
          </Shell>
        }
      />
      <Route
        path="/permission-sets/:id"
        element={
          <Shell>
            <PermissionSetDetailPage />
          </Shell>
        }
      />
      {/*
        The API keys that let another SYSTEM call this API — today the public
        event-interest form, whose server posts submissions with one.

        ⛔ **SUPER-ADMIN ON THE SERVER, not merely in this app — and the
        distinction decides what a later pass may do with it.** Every route in
        `api-keys.routes.ts` carries `requireRole(...SUPER_ADMIN_ROLES)`, so
        below that tier every request here 403s. That is NOT the
        `/permission-sets` shape directly above, where the API allows any admin
        and this app is stricter by choice; here the sidebar row's
        `superAdminOnly` matches the server rather than narrowing it, and
        loosening it to `adminOnly` would lead an administrator to a page whose
        every request refuses them.

        ⚠️ **The route still carries no guard**, on this file's standing rule: a
        hidden row is an optimisation and the 403 is the guarantee, so somebody
        arriving by URL must meet the page's own refusal rather than a redirect
        or a blank screen.

        ⛔ **A KEY'S PLAINTEXT TOKEN IS RETURNED ONCE, AT CREATION, AND IS NEVER
        STORED** — only its hash and a short prefix are kept, and the prefix is
        what identifies a key on screen afterwards. So nothing under this route
        can show a token a second time, and "revoke" sets a timestamp rather than
        deleting the row: a key that could be read back out of the database would
        be a password in a table.

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — this page must not
        add one of its own.
      */}
      <Route
        path="/api-keys"
        element={
          <Shell>
            <ApiKeysPage />
          </Shell>
        }
      />
      {/*
        The two look-up tables the public 2027 interest form resolves against —
        `event_key` → a project, `city_key` → a city.

        🔴 **UNTIL A MAPPING EXISTS THE FORM CANNOT BE SUBMITTED.** A key that
        resolves to nothing is a 422 at submission time, so an unmapped key is
        not a blank cell somewhere in EMERGE — it is a customer pressing Send and
        being refused. Before this page there was no way for a person to enter
        one at all: no screen and no endpoint, only SQL against production.

        ⛔ **ADMIN ON THE SERVER, ON THE READ AS WELL AS THE WRITES.** Every
        route on `/event-maps` carries `requireRole(...ADMIN_ROLES)`, so below
        that tier the only possible answer is a refusal — the `/audit-log` and
        `/settings/smtp` shape, NOT the `/integrations` one. That is why the
        sidebar row is `adminOnly` rather than gated on a permission module:
        there is no `event-maps` module in the catalogue, and inventing a slug
        would resolve to nothing and DEGRADE OPEN, handing the row back to
        everybody it exists to keep out.

        ⚠️ **The route carries no guard**, on this file's standing rule: a hidden
        row is an optimisation and the 403 is the guarantee, so somebody arriving
        by URL meets the page's own refusal rather than a redirect or a blank
        screen — and the page issues NO request in that state, because two empty
        tables would read as "nothing is mapped".

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — this page must not
        add one of its own.
      */}
      <Route
        path="/event-maps"
        element={
          <Shell>
            <EventMapsPage />
          </Shell>
        }
      />
      {/*
        The look-up table a B2B proposal's service keys resolve against —
        the sender's own product slug → a SEED EMERGE product.

        🔴 **AND AN UNMAPPED KEY REFUSES NOTHING — THE OPPOSITE OF THE ROUTE
        ABOVE.** On `/event-maps` a key resolving to nothing is a 422 at
        submission time. Here the owner ruled that a proposal is never refused
        for want of a mapping: the key is stored verbatim, the join is LEFT, and
        the service comes back unresolved. `f_b2b_proposal_service_map` ships
        EMPTY and that is a legal day-one state, so **nothing under this route
        may be worded as a fault or a countdown.** What filling it buys is a
        proposal reading a product name instead of a raw slug.

        ⛔ **Before this page there was no way for a person to enter a row at
        all** — no screen and no form, only SQL against production.

        ⚠️ **ADMIN ONLY, and the sidebar row is `adminOnly` with NO `module`
        slug** — there is no `b2b-service-maps` permission in the catalogue, and
        an invented slug resolves to nothing and DEGRADES OPEN, handing the row
        back to everybody it exists to keep out. Same reasoning as `Event Maps`,
        `Users` and `Audit Log`.

        ⚠️ **The route carries no guard**, on this file's standing rule: a hidden
        row is an optimisation and the refusal is the guarantee, so somebody
        arriving by URL meets the page's own refusal rather than a redirect or a
        blank screen — and the page issues NO request in that state, because an
        empty table would read as "nothing is mapped", which is the one statement
        this screen exists to make truthfully.

        ⚠️ **`Shell`, so `AppShell` renders the `<main>`** — this page must not
        add one of its own.
      */}
      <Route
        path="/b2b-service-maps"
        element={
          <Shell>
            <B2bServiceMapsPage />
          </Shell>
        }
      />
      {/* The caller's own task queue. Open to every role — no role guard, unlike every other write in the API. */}
      <Route
        path="/tasks"
        element={
          <Shell>
            <TasksPage />
          </Shell>
        }
      />
      {/*
        The brand: the mark, the palette, the type and the rules.

        ⚠️ NO NAV ROW, DELIBERATELY — reached by URL, like `/cycles` and
        `/intakes`. Adding one is an IA decision about which group it belongs to,
        not a side effect of building the page, and this file is not the place to
        take it. Say so rather than adding a row quietly.

        Open to every role, and it carries no `requireRole`: it documents
        artwork, not data. Nothing on it reads the API.
      */}
      <Route
        path="/brand"
        element={
          <Shell>
            <BrandPage />
          </Shell>
        }
      />
      {/*
        The sales tracker: one lane per project in a cycle, one card per company
        being pursued for it.

        ⚠️ NO GUARD ON THE ROUTE, like `/tasks` above — `sales-tracker` is a real
        server-enforced module, and the page renders its own refusal so somebody
        arriving by URL gets a sentence rather than a blank screen.
      */}
      <Route
        path="/sales-tracker"
        element={
          <Shell>
            <SalesTrackerPage />
          </Shell>
        }
      />
      {/*
        Personal notification switches, from the account menu. `?userId=` opens
        another person's switches (Users row action); the server refuses that
        with a 403 unless the caller holds `users.update`.
      */}
      <Route
        path="/notifications"
        element={
          <Shell>
            <NotificationsPage />
          </Shell>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
